import * as React from 'react';
import {
  Dialog,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  Divider,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';


import MainCard from '../../Components/Dashboard/MainCard.js';
import PkgUsageChart from './PkgUsageChart';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

export default function PkgStatsDetailsDialog({ detailsInfo, handleClose }) {
  const [timeFilter, setTimeFilter] = React.useState('monthly');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [sortConfig, setSortConfig] = React.useState({
    key: 'accessed_date',
    direction: 'desc',
  });

  // ✅ NEW: Users dialog state
  const [openUsersDialog, setOpenUsersDialog] = React.useState(false);

  const usage_recordsPerPage = 10;
  const descriptionElementRef = React.useRef(null);

  React.useEffect(() => {
    if (detailsInfo.open) {
      descriptionElementRef.current?.focus();
    }
  }, [detailsInfo.open]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
    setPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc'
        ? <ArrowUpwardIcon fontSize="small" />
        : <ArrowDownwardIcon fontSize="small" />;
    }
    return <UnfoldMoreIcon fontSize="small" />;
  };

  const sortedAndFilteredRecords = React.useMemo(() => {
    if (!detailsInfo.data?.usage_records) return [];

    let records = [...detailsInfo.data.usage_records];
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      records = records.filter(r =>
        `${r.user_name} ${r.pack_name} ${r.number_of_hits}`.toLowerCase().includes(q)
      );
    }

    records.sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'accessed_date':
          aVal = new Date(a.accessed_date);
          bVal = new Date(b.accessed_date);
          break;
        case 'number_of_hits':
          aVal = +a.number_of_hits;
          bVal = +b.number_of_hits;
          break;
        case 'user_name':
          aVal = a.user_name.toLowerCase();
          bVal = b.user_name.toLowerCase();
          break;
        case 'pack_name':
          aVal = a.pack_name.toLowerCase();
          bVal = b.pack_name.toLowerCase();
          break;
        default:
          return 0;
      }
      return sortConfig.direction === 'asc'
        ? aVal > bVal ? 1 : -1
        : aVal < bVal ? 1 : -1;
    });

    return records;
  }, [detailsInfo.data, searchTerm, sortConfig]);

  const paginated = sortedAndFilteredRecords.slice(
    (page - 1) * usage_recordsPerPage,
    page * usage_recordsPerPage
  );

  const totalPages = Math.ceil(sortedAndFilteredRecords.length / usage_recordsPerPage);

  const renderContent = () => {
    if (!detailsInfo.data?.usage_records?.length) {
      return (
        <Typography align="center" color="text.secondary" sx={{ mt: 6 }}>
          No package statistics available
        </Typography>
      );
    }

    const { usage_records } = detailsInfo.data;
    const first = usage_records[0];
    const totalUsage = usage_records.reduce((s, r) => s + +r.number_of_hits, 0);
    const uniqueUsers = [...new Set(usage_records.map(r => r.user_name))];

    return (
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* ===== KPI CARDS ===== */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 3,
          }}
        >
          <MainCard sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #f0f4ff, #d9e3ff)' }}>
            <Inventory2Icon sx={{ fontSize: 32, color: '#28357e' }} />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Package</Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>{first.pack_name}</Typography>
          </MainCard>

          {/* ✅ USERS CARD WITH INFO ICON */}
          <MainCard
            sx={{
              p: 2,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
              position: 'relative',
            }}
          >
            <PeopleAltIcon sx={{ fontSize: 32, color: '#2e7d32' }} />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Users</Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>{uniqueUsers.length}</Typography>

            <IconButton
              size="small"
              onClick={() => setOpenUsersDialog(true)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(255,255,255,0.85)',
                '&:hover': { background: 'rgba(255,255,255,1)' },
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </MainCard>

          <MainCard sx={{ p: 2, textAlign: 'center', background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)' }}>
            <BarChartIcon sx={{ fontSize: 32, color: '#ff9800' }} />
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Total Usage</Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>{totalUsage}</Typography>
          </MainCard>
        </Box>

        {/* ===== USERS DIALOG ===== */}
        <Dialog
          open={openUsersDialog}
          onClose={() => setOpenUsersDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Users</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {uniqueUsers.map((u) => (
                <Typography key={u} variant="body2" sx={{ mb: 0.5 }}>
                  • {u}
                </Typography>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenUsersDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ===== CHART ===== */}
        <MainCard sx={{ p: 3 }}>
          {/* <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
            Usage Over Time
          </Typography>
          <Divider sx={{ mb: 3 }} /> */}
          <PkgUsageChart
            data={detailsInfo.data}
            timeFilter={timeFilter}
            onTimeFilterChange={setTimeFilter}
          />
        </MainCard>

        {/* ===== TABLE HEADER ===== */}
        <Box display="flex" justifyContent="space-between" mb={2}>
          
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>
            Daily Activity Metrics
          </Typography>
          <TextField
            size="small"
            placeholder="Search user, package..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ===== TABLE ===== */}
        <TableContainer
        component={Paper}
        sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
        }}
        >
        <Table stickyHeader>
            {/* ===== TABLE HEAD ===== */}
            <TableHead>
            <TableRow>
                {['accessed_date', 'number_of_hits', 'user_name', 'pack_name'].map((key, i) => (
                <TableCell
                    key={key}
                    onClick={() => handleSort(key)}
                    sx={{
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    backgroundColor: '#f9fafb',
                    color: '#1f2937',
                    borderBottom: '1px solid #e5e7eb',
                    position: 'relative',

                    '&:hover': {
                        backgroundColor: '#eef2ff',
                    },

                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '25%',
                        height: '50%',
                        width: '1px',
                        backgroundColor: '#e5e7eb',
                    },
                    }}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                    {['Date', 'Usage', 'User', 'Package'][i]}
                    {getSortIcon(key)}
                    </Box>
                </TableCell>
                ))}
            </TableRow>
            </TableHead>

            {/* ===== TABLE BODY ===== */}
            <TableBody>
            {paginated.map((r, rowIndex) => (
                <TableRow
                key={rowIndex}
                hover
                sx={{
                    transition: 'background-color 0.2s ease',

                    '&:hover td': {
                    backgroundColor: '#f8fafc',
                    },
                }}
                >
                <TableCell
                    sx={{
                    fontSize: '0.88rem',
                    borderBottom: '1px solid #f1f5f9',
                    }}
                >
                    {new Date(r.accessed_date).toLocaleDateString()}
                </TableCell>

                <TableCell
                    sx={{
                    borderBottom: '1px solid #f1f5f9',
                    }}
                >
                    <Chip
                    label={r.number_of_hits}
                    size="small"
                    sx={{
                        fontWeight: 600,
                        backgroundColor: '#eef2ff',
                        color: '#3730a3',
                    }}
                    />
                </TableCell>

                <TableCell
                    sx={{
                    fontSize: '0.88rem',
                    fontWeight: 500,
                    borderBottom: '1px solid #f1f5f9',
                    }}
                >
                    {r.user_name}
                </TableCell>

                <TableCell
                    sx={{
                    fontSize: '0.88rem',
                    color: '#374151',
                    borderBottom: '1px solid #f1f5f9',
                    }}
                >
                    {r.pack_name}
                </TableCell>
                </TableRow>
            ))}
            </TableBody>
        </Table>
        </TableContainer>


        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog fullScreen open={detailsInfo.open} onClose={handleClose} TransitionComponent={Transition}>
      <Toolbar sx={{ background: 'linear-gradient(135deg, #3f51b5, #303f9f)', color: '#fff' }}>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
        <Typography variant="h4" sx={{ ml: 2, fontWeight: 700 }}>
          Package Analytics
        </Typography>
      </Toolbar>

      <DialogContent sx={{ px: { xs: 3, sm: 6 }, py: 4 }}>
        <Box ref={descriptionElementRef}>{renderContent()}</Box>
      </DialogContent>
    </Dialog>
  );
}
